import { useEffect, useMemo } from 'react'
import { useFetchEtfsByTags } from '@/trading/tradingQueries'

const PAGE_SIZE = 10

function normalizePage(page?: number): number {
    if (!page || page < 1 || Number.isNaN(page)) return 1
    return Math.floor(page)
}

function inferTotalPagesFromResponse(data: any, page: number, listLength: number): number | undefined {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        const totalItems = (data.total ?? data.totalItems ?? data.totalResults ?? data.count) as number | undefined
        if (typeof totalItems === 'number' && totalItems >= 0) {
            return Math.max(1, Math.ceil(totalItems / PAGE_SIZE))
        }
    }

    if (listLength === 0 && page > 1) return page - 1
    if (listLength > 0 && listLength < PAGE_SIZE) return page
    return undefined
}

export function useEtfsSearchModel(
    positiveTags: string[],
    negativeTags: string[],
    enabled = true,
    pageParam?: number,
    onPageChange?: (p: number) => void,
) {
    const page = normalizePage(pageParam)

    const { data: etfsByTag, refetch, isLoading } = useFetchEtfsByTags(positiveTags, negativeTags, page, enabled)

    const rawItems = useMemo(() => {
        if (!etfsByTag) return []
        return Array.isArray(etfsByTag) ? etfsByTag : etfsByTag.items || []
    }, [etfsByTag])

    const totalPages = useMemo(
        () => inferTotalPagesFromResponse(etfsByTag, page, rawItems.length),
        [etfsByTag, page, rawItems.length],
    )

    useEffect(() => {
        if (!etfsByTag) return
        if (!onPageChange) return
        if (rawItems.length !== 0 || page <= 1) return

        onPageChange(page - 1)
    }, [etfsByTag, rawItems.length, page, onPageChange])

    const items = useMemo(() => {
        const mapped = rawItems
            .map((etf: any) => {
                const positive = etf.positiveTags || etf.positiveThemesIncludedCollection?.items || []
                const avoided = etf.avoidedTags || etf.avoidedCollection?.items || []

                const ethicalTags = [
                    ...positive.map((p: any) => ({ tag: p.positiveThemesIncluded ?? p.tag ?? p, isTagPositive: true })),
                    ...avoided.map((a: any) => ({ tag: a.avoided ?? a.tag ?? a, isTagPositive: false })),
                ]

                return {
                    securityDetails: etf.securityDetails || etf.company || undefined,
                    securityName: etf.securityName || etf.company?.companyName,
                    securityProfile: etf.securityProfile,
                    historicalPrices: etf.historicalPrices,
                    ethicalTags,
                    changeInPriceYTD: Number(etf.changeInPriceYTD) || 0,
                    raw: etf,
                }
            })
            .filter((security: any) => security && security.securityDetails && security.securityDetails.priceInfo)

        return mapped
    }, [rawItems])

    const setPage = (p: number) => {
        if (onPageChange) onPageChange(normalizePage(p))
    }

    return { items, isLoading, refetch, currentPage: page, setPage, totalPages }
}

export default useEtfsSearchModel
